import { useState, useCallback, useEffect, useMemo } from 'react';
import { AppliedModifierDropdownData, AppliedModifierPanelData, createDefaultDropdownData, createDefaultPanelData, UI_CONSTANTS } from '../constant/applied_modifiers_constants';
import { AppliedModifiersDataService, IAppliedModifiersDataService, DefaultApiClient } from '../services/AppliedModifiersDataService';

/**
 * Interface for data loading operations
 */
export interface IDataLoader {
  loading: boolean;
  dropdownData: AppliedModifierDropdownData;
  panelData: AppliedModifierPanelData;
  loadData: () => Promise<void>;
  error: string | null;
}

/**
 * Custom hook for managing Applied Modifiers data loading
 */
export const useAppliedModifiersData = (
  userLoginDetails: any,
  isEdit: boolean = false,
  modifierList?: any[],
  toChargeByList?: any[],
  primarySpecialtyList?: any[],
  payerList?: any[],
  insuranceClassList?: any[]
): IDataLoader => {
  const [loading, setLoading] = useState(false);
  const [dropdownData, setDropdownData] = useState<AppliedModifierDropdownData>(createDefaultDropdownData());
  const [panelData, setPanelData] = useState<AppliedModifierPanelData>(createDefaultPanelData());
  const [error, setError] = useState<string | null>(null);

  // Initialize data service - memoize to prevent infinite loops
  const accessToken = (userLoginDetails && 
    userLoginDetails.user_data && 
    userLoginDetails.user_data.data && 
    userLoginDetails.user_data.data.token_details && 
    userLoginDetails.user_data.data.token_details.access_token) || '';
  
  const dataService: IAppliedModifiersDataService = useMemo(() => 
    new AppliedModifiersDataService(
      new DefaultApiClient(accessToken),
      accessToken
    ), [accessToken]
  );

  const loadData = useCallback(async () => {
    // Check which props are provided (will follow a lazy loading approach for all the lists that are not provided)
    const hasProvidedModifierList = modifierList && modifierList.length > 0;
    const hasProvidedToChargeByList = toChargeByList && toChargeByList.length > 0;
    const hasProvidedPrimarySpecialtyList = primarySpecialtyList && primarySpecialtyList.length > 0;
    const hasProvidedPayerList = payerList && payerList.length > 0;
    const hasProvidedInsuranceClassList = insuranceClassList && insuranceClassList.length > 0;

    // If all props are provided, use them directly without calling services
    if (hasProvidedModifierList && hasProvidedToChargeByList && hasProvidedPrimarySpecialtyList && 
        hasProvidedPayerList && hasProvidedInsuranceClassList) {
      setLoading(false);
      setError(null);

      // Process states from user login details
      const formatedMasterData = userLoginDetails && userLoginDetails.formated_master_data;
      const states = formatedMasterData && formatedMasterData.states;
      let stateList: any[] = [];

      if (states && Array.isArray(states)) {
        stateList = states.map((state: any) => ({
          name: state.text,
          value: state.value
        }));
      }

      // Create default dropdown item for modifiers
      const defaultDropdownItem = {
        text: UI_CONSTANTS.DEFAULT_VALUES.DROPDOWN_PLACEHOLDER_TEXT,
        value: UI_CONSTANTS.DEFAULT_VALUES.DROPDOWN_DEFAULT_VALUE,
        key: UI_CONSTANTS.DEFAULT_VALUES.DROPDOWN_PLACEHOLDER_KEY,
        code: UI_CONSTANTS.DEFAULT_VALUES.DROPDOWN_DEFAULT_CODE
      };

      setDropdownData({
        modifier_list: [defaultDropdownItem, ...(modifierList || [])],
        to_charge_by_list: toChargeByList || [],
        state_list: stateList,
        primary_specialty_list: primarySpecialtyList || []
      });

      setPanelData({
        payer_list: payerList || [],
        insurance_class_list: insuranceClassList || []
      });
      return;
    }

    // Check if we need to call any services
    const needsServiceCall = !hasProvidedModifierList || !hasProvidedToChargeByList || 
                             !hasProvidedPrimarySpecialtyList || !hasProvidedPayerList || 
                             !hasProvidedInsuranceClassList;

    if (needsServiceCall) {
      // Validate user login details if we need to call services
      if (!userLoginDetails || !userLoginDetails.user_data || !userLoginDetails.user_data.data || !userLoginDetails.user_data.data.company_id) {
        setError('User login details not available');
        return;
      }

      setLoading(true);
      setError(null);

      try {
        // Build array of promises only for missing data
        const promises: Promise<any>[] = [];
        const promiseIndices = {
          modifierList: -1,
          toChargeByList: -1,
          primarySpecialtyList: -1,
          payerList: -1,
          insuranceClassList: -1
        };

        if (!hasProvidedModifierList) {
          promiseIndices.modifierList = promises.length;
          promises.push(dataService.getModifiersList(userLoginDetails.user_data.data.company_id));
        }

        if (!hasProvidedToChargeByList) {
          promiseIndices.toChargeByList = promises.length;
          promises.push(dataService.getToChargeByList(userLoginDetails.user_data.data.company_id));
        }

        if (!hasProvidedPrimarySpecialtyList) {
          promiseIndices.primarySpecialtyList = promises.length;
          promises.push(dataService.getPrimarySpecialtyList());
        }

        if (!hasProvidedPayerList) {
          promiseIndices.payerList = promises.length;
          promises.push(dataService.getPayerList());
        }

        if (!hasProvidedInsuranceClassList) {
          promiseIndices.insuranceClassList = promises.length;
          promises.push(dataService.getInsuranceClassList());
        }

        // Fetch only missing data
        const fetchedResults = promises.length > 0 ? await Promise.all(promises) : [];

        // Use provided props if available, otherwise use fetched data
        const finalModifierList = hasProvidedModifierList 
          ? modifierList 
          : (promiseIndices.modifierList >= 0 ? fetchedResults[promiseIndices.modifierList] : []);
        
        const finalToChargeByList = hasProvidedToChargeByList 
          ? toChargeByList 
          : (promiseIndices.toChargeByList >= 0 ? fetchedResults[promiseIndices.toChargeByList] : []);
        
        const finalPrimarySpecialtyList = hasProvidedPrimarySpecialtyList 
          ? primarySpecialtyList 
          : (promiseIndices.primarySpecialtyList >= 0 ? fetchedResults[promiseIndices.primarySpecialtyList] : []);
        
        const finalPayerList = hasProvidedPayerList 
          ? payerList 
          : (promiseIndices.payerList >= 0 ? fetchedResults[promiseIndices.payerList] : []);
        
        const finalInsuranceClassList = hasProvidedInsuranceClassList 
          ? insuranceClassList 
          : (promiseIndices.insuranceClassList >= 0 ? fetchedResults[promiseIndices.insuranceClassList] : []);

        // Process states from user login details
        const formatedMasterData = userLoginDetails.formated_master_data;
        const states = formatedMasterData && formatedMasterData.states;
        let stateList: any[] = [];

        if (states && Array.isArray(states)) {
          stateList = states.map((state: any) => ({
            name: state.text,
            value: state.value
          }));
        }

        // Create default dropdown item for modifiers
        const defaultDropdownItem = {
          text: UI_CONSTANTS.DEFAULT_VALUES.DROPDOWN_PLACEHOLDER_TEXT,
          value: UI_CONSTANTS.DEFAULT_VALUES.DROPDOWN_DEFAULT_VALUE,
          key: UI_CONSTANTS.DEFAULT_VALUES.DROPDOWN_PLACEHOLDER_KEY,
          code: UI_CONSTANTS.DEFAULT_VALUES.DROPDOWN_DEFAULT_CODE
        };

        setDropdownData({
          modifier_list: [defaultDropdownItem, ...(finalModifierList || [])],
          to_charge_by_list: finalToChargeByList || [],
          state_list: stateList,
          primary_specialty_list: finalPrimarySpecialtyList || []
        });

        setPanelData({
          payer_list: finalPayerList || [],
          insurance_class_list: finalInsuranceClassList || []
        });
      } catch (err) {
        setError('Failed to load data');
        console.error('Error loading data:', err);
      } finally {
        setLoading(false);
      }
    } else {
      // All props provided, but we already handled this case above
      // This else block should not be reached, but kept for safety
      setLoading(false);
    }
  }, [userLoginDetails, dataService, modifierList, toChargeByList, primarySpecialtyList, payerList, insuranceClassList]);

  // Load data on mount
  useEffect(() => {
    loadData();
  }, [loadData]);

  return {
    loading,
    dropdownData,
    panelData,
    loadData,
    error
  };
};
